import SendIcon from "@mui/icons-material/Send";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { DiscoveryPromptDetails } from "../components/discovery-prompt-details.tsx";
import { DiscoveryRunLogs } from "../components/discovery-run-logs.tsx";
import { DiscoveryRunStateChip } from "../components/discovery-run-state-chip.tsx";
import { IssueTable } from "../components/issue-table.tsx";
import { LiveView } from "../components/live-view.tsx";
import { MarkdownBlock } from "../components/markdown-block.tsx";
import { SessionCard } from "../components/session-card.tsx";
import { TestReviewSwiper } from "../components/test-review-swiper.tsx";
import { UserStoriesPanel } from "../components/user-stories-panel.tsx";
import { VideoPlayer } from "../components/video-player.tsx";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import type { EventCreateSchema } from "../types/events.ts";
import type { DiscoveryPromptDoneIntent, TestAgentV2Session } from "../types/test-agent-2.ts";
import type { DiscoveryRun, Issue, Persona } from "../types.ts";
import { formatDuration } from "../utils";

interface DiscoveryRunDetailsV1Tabs {
  tab?: "manager" | "agents" | "issues" | "auto-discovery" | "review" | "user-stories";
}

export default function DiscoveryRunDetailsV1Page() {
  const userSelectedTabRef = useRef(false);

  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();

  const [finished, setFinished] = useState(false);

  const { params, setParam } = useTypedQueryParams<DiscoveryRunDetailsV1Tabs>();

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      userSelectedTabRef.current = true;
      setParam("tab", newValue as DiscoveryRunDetailsV1Tabs["tab"]);
    },
    [setParam],
  );

  const { runId } = useParams();

  const { data, isLoading } = useApiQuery<DiscoveryRun>(`/discovery-runs/${runId}`, {
    enabled: !!runId,
  });

  // Default to "manager" only if run is running, otherwise default to "manager"
  const getDefaultTab = useCallback(() => {
    return "manager";
  }, []);

  const tab = params.tab ?? getDefaultTab();

  const { data: persona } = useApiQuery<Persona>(`/personas/${data?.persona_id}`, {
    enabled: !!data?.persona_id,
  });

  // Fetch auto-discovery session if exists
  const { data: autoDiscoverySession } = useApiQuery<TestAgentV2Session | null>(
    `/discovery-runs/${runId}/auto-discovery-session`,
    {
      enabled: !!runId,
    },
  );

  // Extract the done intent from the auto-discovery session
  const autoDiscoveryDoneIntent = useMemo(() => {
    if (!autoDiscoverySession?.data?.last_done) return null;
    return autoDiscoverySession.data.last_done as DiscoveryPromptDoneIntent;
  }, [autoDiscoverySession]);

  const { mutate: cancelRun, isPending: isCancelling } = useApiMutation<void, void>(
    "POST",
    `/discovery-runs/${runId}/cancel`,
    {
      onSuccess: () => {
        enqueueSnackbar("Discovery run cancellation request sent successfully", {
          variant: "warning",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to cancel run: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
    },
  );

  useEffect(() => {
    setFinished(false);
    userSelectedTabRef.current = false;
    // Set default tab if there's no existing tab in URL
    if (!params.tab) {
      setParam("tab", getDefaultTab());
    }
  }, [setParam, params.tab, getDefaultTab]);

  useEffect(() => {
    if (!data) return;
    if (userSelectedTabRef.current) return; // Don't auto-switch if user selected a tab
    if (finished) return;

    // If run finishes, mark as finished
    if (data.state !== "running") {
      setFinished(true);
    }
  }, [data, finished]);

  const duration = useMemo(() => {
    return formatDuration(data?.elapsed_seconds);
  }, [data?.elapsed_seconds]);

  const [recordingPath, setRecordingPath] = useState<string | null>(null);

  useEffect(() => {
    if (data?.recording_path && !recordingPath) {
      setRecordingPath(data.recording_path);
    }
  }, [data?.recording_path, recordingPath]);

  const [response, setResponse] = useState<string>("");

  const { mutate: newEvent, isPending: isCreatingEvent } = useApiMutation<EventCreateSchema>(
    "POST",
    "/new-event",
    {
      onSuccess: () => {
        setResponse("");
      },
    },
  );

  const { data: issuesData, isLoading: isLoadingIssues } = useApiQuery<Issue[]>(
    `/issues?discovery_run_id=${data?.id}&no_axe=true`,
    {
      enabled: !!data?.id,
    },
  );

  const agentCount = useMemo(() => {
    let count = 0;

    if (data?.version === "v3") {
      count += data?.agent_ids?.length ?? 0;
    } else {
      count += (data?.agent_ids?.length ?? 0) + (data?.analysis_agent_ids?.length ?? 0);
    }

    return count;
  }, [data]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!data) {
    return <Typography>Discovery Run for id "{runId}" not found</Typography>;
  }

  // Show v0 component if this is not a v1 discovery run
  if (!["v1", "v3"].includes(data.version ?? "v0")) {
    return <Typography>This discovery run is not version v1</Typography>;
  }

  return (
    <Box data-testid="discovery-run-details-v1-page" sx={{ width: "100%", height: "100%" }}>
      <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DiscoveryRunStateChip state={data.state} />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            divider={<Typography variant="body1">·</Typography>}
          >
            <Typography variant="body1">
              {new Date(data.started_at ?? data.created_at).toLocaleString()}
            </Typography>
            <Typography variant="body1">
              {data.ended_at ? new Date(data.ended_at).toLocaleString() : "Ongoing"}
            </Typography>
            <Typography variant="body1">{duration ?? "Calculating duration..."}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          {data.external_id && (
            <Tooltip title="Cancel Discovery Run">
              <Button
                variant="outlined"
                color="warning"
                disabled={data.state !== "running" || isCancelling}
                onClick={() => {
                  cancelRun({});
                }}
              >
                Cancel
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={{ xs: 2 }}>
        <Grid size={{ xs: 12 }}>
          <TabContext value={tab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={handleChange} aria-label="Run details tabs">
                <Tab label="Manager" value="manager" />
                <Tab label={`Agents (${agentCount})`} value="agents" />
                <Tab label={`Issues (${issuesData?.length ?? 0})`} value="issues" />
                <Tab label="Review" value="review" />
                {data.version === "v3" && data.app_config_id && (
                  <Tab label="User Stories" value="user-stories" />
                )}
                {autoDiscoverySession && <Tab label="Auto-Discovery" value="auto-discovery" />}
              </TabList>
            </Box>
            <TabPanel value="manager" sx={{ px: 0 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 8 }}>
                  {recordingPath ? (
                    <VideoPlayer
                      src={recordingPath}
                      width="100%"
                      height="500px"
                      controls={true}
                      autoPlay={true}
                      muted={true}
                    />
                  ) : (
                    <LiveView
                      liveViewUrl={data.live_view_url}
                      height="500px"
                      noLiveViewMessage="No live view available yet..."
                    />
                  )}
                  {["done", "closed"].includes(data.state) && !recordingPath && (
                    <Typography variant="body1" color="text.secondary">
                      No recording available yet...
                    </Typography>
                  )}

                  {/* Intent, App, and Persona info below the video */}
                  <Stack direction="column" spacing={2} alignItems="stretch" mt={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {persona && (
                        <Tooltip title="View Persona Details">
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ width: "fit-content" }}
                            onClick={() => nav(`/personas/${persona.id}`)}
                          >
                            <Typography variant="body2" noWrap>
                              {persona.name}
                            </Typography>
                          </Button>
                        </Tooltip>
                      )}
                      {data.state === "user_input_needed" && (
                        <TextField
                          label="Reply to the manager..."
                          variant="outlined"
                          size="small"
                          sx={{ flexGrow: 1 }}
                          placeholder="Shift + Enter for new line, Enter to send"
                          type="text"
                          multiline
                          minRows={1}
                          maxRows={3}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => {
                                      if (response.trim().length === 0 || isCreatingEvent) {
                                        return;
                                      }

                                      newEvent({
                                        group_key: data.id,
                                        event_type: "test_agent_2.event.incoming",
                                        event_data: {
                                          type: "response",
                                          message: response.trim(),
                                        },
                                      });
                                    }}
                                    edge="end"
                                    size="small"
                                    disabled={isCreatingEvent || response.trim().length === 0}
                                  >
                                    <SendIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                          value={response}
                          onChange={(e) => setResponse(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();

                              if (response.trim().length === 0 || isCreatingEvent) {
                                return;
                              }

                              newEvent({
                                group_key: data.id,
                                event_type: "test_agent_2.event.incoming",
                                event_data: {
                                  type: "response",
                                  message: response.trim(),
                                },
                              });
                            }
                          }}
                        />
                      )}
                    </Stack>
                    <Card sx={{ flexGrow: 1, p: 2 }}>
                      {data.intent ? (
                        <Box
                          sx={{
                            whiteSpace: "pre-line",
                            maxHeight: "300px",
                            overflowY: "auto",
                            width: "100%",
                          }}
                        >
                          <MarkdownBlock content={data.intent} />
                        </Box>
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No intent provided for this discovery run.
                        </Typography>
                      )}
                      {data.intent && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="right"
                          sx={{ mt: 1 }}
                        >
                          {data.intent.length} characters and {data.intent.split("\n").length} lines
                        </Typography>
                      )}
                    </Card>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <DiscoveryRunLogs runId={data.id} />
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value="agents" sx={{ px: 0 }}>
              {tab === "agents" && (
                <Grid container spacing={2} mt={1}>
                  {/* Test agents first */}
                  {data?.agent_ids?.map((agentId) => (
                    <Grid key={agentId} size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
                      <SessionCard sessionId={agentId} />
                    </Grid>
                  ))}
                  {/* Analysis agents second */}
                  {data.version !== "v3" &&
                    data?.analysis_agent_ids?.map((agentId) => (
                      <Grid key={agentId} size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
                        <SessionCard sessionId={agentId} />
                      </Grid>
                    ))}
                  {(!data?.agent_ids || data.agent_ids.length === 0) &&
                    (!data?.analysis_agent_ids || data.analysis_agent_ids.length === 0) && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          No agent sessions found for this discovery run.
                        </Typography>
                      </Grid>
                    )}
                </Grid>
              )}
            </TabPanel>
            <TabPanel value="issues" sx={{ px: 0 }}>
              {tab === "issues" && (
                <IssueTable data={issuesData ?? []} isLoading={isLoadingIssues} height={600} />
              )}
            </TabPanel>
            {/* Review Tab - Swiper only */}
            <TabPanel value="review" sx={{ p: 0, pt: 2, height: "calc(100vh - 160px)" }}>
              {tab === "review" && (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                  }}
                >
                  <TestReviewSwiper discoveryRunId={runId!} maxWidth={720} />
                </Box>
              )}
            </TabPanel>

            {/* User Stories Tab - v3 only */}
            {data.version === "v3" && data.app_config_id && (
              <TabPanel value="user-stories" sx={{ px: 0 }}>
                {tab === "user-stories" && <UserStoriesPanel appConfigId={data.app_config_id} />}
              </TabPanel>
            )}

            {autoDiscoverySession && autoDiscoveryDoneIntent && (
              <TabPanel value="auto-discovery" sx={{ px: 0, pt: 2 }}>
                {tab === "auto-discovery" && (
                  <Card sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}
                    >
                      Auto-Discovery Details
                    </Typography>
                    <DiscoveryPromptDetails doneIntent={autoDiscoveryDoneIntent} />
                  </Card>
                )}
              </TabPanel>
            )}
          </TabContext>
        </Grid>
      </Grid>
    </Box>
  );
}
